import { useEffect, useState } from 'react';
import { User } from 'lib/types/users';
import { useCurrentUser } from 'lib/contexts/ApplicationState';
import { Role } from 'lib/types';
import { Credit } from 'lib/types/credits';
import { userIsBeta, userIsPro } from 'lib/helpers';
import { PrivateCheckBox } from 'components/PrivateCheckBox';
import { RolePills } from 'components/RolePills';

interface Props {
  postId: number;
  credits: Credit[];
  credit: Credit;
  hidePrivate?: boolean;
  saveLabel?: string;
  onSave: (user: User, roles: Role[], isPrivate?: boolean) => void;
  onRemove?: () => void;
  onCancel?: () => void;
}

export const EditCreditForm = ({
  saveLabel,
  postId,
  credits,
  credit,
  hidePrivate,
  onSave,
  onRemove,
  onCancel,
}: Props) => {
  const currentUser = useCurrentUser();

  const [isSaving, setIsSaving] = useState(false);

  const [selected, setSelected] = useState<User>();
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);
  const [isPrivate, setIsPrivate] = useState(false);

  const showPrivate = !hidePrivate && (userIsBeta(currentUser) || userIsPro(currentUser));
  const disable = isSaving || !selected || selectedRoles.length <= 0;

  const save = () => {
    if (!selected || selectedRoles.length <= 0) return;

    setIsSaving(true);
    if (onSave) {
      onSave(selected, selectedRoles, isPrivate);
    }
    setIsSaving(false);
  };

  const cancel = () => {
    setSelectedRoles([]);
    if (onCancel) onCancel();
  };

  const selectRoles = (roles: Role[]) => {
    setSelectedRoles(roles);
    setIsSaving(false);
  };

  useEffect(() => {
    const init = async (c: Credit) => {
      setSelected(c.user);
      setSelectedRoles([c.role]);
      setIsPrivate(c.isPrivate);
    };

    if (credit) {
      init(credit);
    }
  }, [postId, credits, credit]);

  if (!selected) return null;

  return (
    <div className="relative py-4 border-dashed border-t">
      <h5 className="font-semibold text-xl w-1/2 pb-2">
        Edit Credit:
        <span className="font-bold ml-2">{credit?.user?.name}</span>
      </h5>
      <div className="mb-4">
        <h6 className="font-semibold text-xl mb-2">
          Role
        </h6>
        <RolePills
          user={selected}
          selectedRoles={selectedRoles}
          onChange={selectRoles}
        />
      </div>
      {showPrivate && (
        <PrivateCheckBox
          isPrivate={isPrivate}
          onChange={setIsPrivate}
        />
      )}
      <div className="flex flex-col-reverse md:flex-row justify-between gap-2 mt-4 w-full">
        <div className="text-center">
          {onRemove && (
            <button
              type="button"
              className="btn-primary-outlined border-none sm:border-solid"
              aria-label="delete credit"
              onClick={onRemove}
            >
              Delete Credit
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <div className="flex w-full sm:w-auto gap-2">
            <button type="button" className="btn-primary-outlined w-1/2 sm:w-auto" onClick={cancel}>Cancel</button>
            <button
              type="button"
              className="btn-primary w-1/2 sm:w-auto"
              disabled={disable}
              onClick={save}
            >
              {saveLabel || 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
